import {
  Card,
  Chips,
  ItemStatus,
  ButtonMoreOptions,
  ModalConfirmation,
  ModalCustomRefund,
} from 'components';
import { Check, Copy } from 'lucide-react';
import { useSubscriptions } from 'pages/Settings/pages/Subscriptions/hooks/Subscriptions.hook';
import {
  ICompany,
  IProductDetails,
  Subscription,
  SubscriptionPayment,
} from 'shared/services/user-subscription';
import { useEffect, useRef, useState } from 'react';
import './Item-subscription.scss';
import { useProductTitle } from './hooks/Item-Subscription.hook';
import { useSubscriptionActions } from './hooks/useSubscriptionActions';
import { useSubscriptionCancelOptions } from './hooks/useSubscriptionCancelOptions';
import { usePricesNotSpecial } from 'hooks/usePricesNotSpecial';
import { useSelector } from 'react-redux';
import { IAppReducer } from 'models';
import { Images } from 'assets/images/images.index';

interface ItemSubscriptionProps {
  id: string;
  dbId: number;
  version: number;
  products: string[];
  price: number;
  dateInit: string;
  dateInitRaw: string;
  dateExpire: string;
  dateExpireRaw: string;
  typePeriodicity: string;
  status: 'successful' | 'error' | 'pending';
  hasTrial: boolean;
  handleEdit?: (id: string) => void;
  uid: string;
  userImpersonationId: string;
  onSuccess?: () => void;
  onError?: (error: string) => void;
  onRenewal?: (subscriptionId: string) => void | Promise<void>;
  totalSubsActive: number;
  productsDetails: IProductDetails[];
  payments: SubscriptionPayment[];
  providerProcessor: string;
  wasMigrated: boolean;
  calculateAmount: boolean;
  company?: ICompany;
  isInsurance?: boolean;
}

const messageStatus = {
  successful: 'Activa',
  error: 'Inactiva',
  pending: 'Vencida',
  cancelada: 'Cancelada',
};

const getProviderImage = (providerProcessor: string): string | null => {
  if (!providerProcessor) return null;

  const provider = providerProcessor.toLowerCase();

  switch (provider) {
    case 'stripe':
      return Images.stripe;
    case 'dlocal':
      return Images.dlocal;
    case 'ath movil':
      return Images.ath_movil;
    case 'cash':
      return Images.cash;
    default:
      return null;
  }
};

export const ItemSubscription = ({
  id,
  dbId,
  version,
  products,
  price,
  dateExpire,
  typePeriodicity,
  status,
  uid,
  userImpersonationId,
  hasTrial,
  onSuccess,
  onError,
  onRenewal,
  productsDetails,
  providerProcessor,
  company,
  isInsurance = false,
}: ItemSubscriptionProps) => {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  const { navigate } = useSubscriptions();
  const productTitle = useProductTitle(products);
  const { branchReducer, authReducer: user } = useSelector(
    (state: IAppReducer) => state
  );

  const cancelOptions = useSubscriptionCancelOptions({
    status,
    branch: branchReducer.branch ?? '',
    hasTrial,
    providerProcessor,
    isInsurance,
  });
  const { hasSpecialPrice } = usePricesNotSpecial();

  const shouldShowCancelButtons =
    status.toLowerCase() === 'successful' ||
    status.toLowerCase() === 'error' ||
    status.toLowerCase() === 'pending';

  // // Verificar si algún pago activo tiene descuento
  // const hasActivePaymentWithDiscount = activePayments.some(
  //   (payment) => payment.has_discount === true
  // );

  // Obtener imagen del proveedor de pago
  const providerImage = getProviderImage(providerProcessor);

  const {
    isLoading,
    showWithoutRefundModal,
    showCustomRefundModal,
    showConfirmationModal,
    confirmationData,
    handleWithoutRefundClick,
    handleCustomRefundClick,
    handleCustomRefundConfirm,
    handleWithoutRefundConfirm,
    executeCancellation,
    closeAllModals,
  } = useSubscriptionActions({
    subscriptionId: id,
    uid,
    userImpersonationId,
    onSuccess,
    onError,
  });

  const handleSubscriptionClick = (
    subId: Subscription['id'],
    subVersion: number
  ) => {
    navigate(`/settings/subscriptions/${subId}/version/${subVersion}`);
  };

  const isPlanMigratedPriceSpecialNew = (): boolean => {
    const nonAddonProducts = productsDetails.filter(
      (product) => !product.isAddon
    );
    return nonAddonProducts.some((product) => hasSpecialPrice(product.amount));
  };

  const handleEditClick = (subId: Subscription['id'], subVersion: number) => {
    if (isPlanMigratedPriceSpecialNew()) {
      // Si es un plan migrado con precio especial, redirigir a edit-plan
      // La ruta espera: /edit-plan/{planId}/{subscriptionId}
      // donde planId = dbId (ID de BD) y subscriptionId = stripe_subscription_id
      navigate(`/edit-plan/${dbId}/${subId}?isPriceSpecial=true`);
    } else {
      navigate(`/settings/subscriptions/edit/${subId}/version/${subVersion}`);
    }
  };

  function handleRenewalClick(subscriptionId: string): void {
    onRenewal?.(subscriptionId);
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
        copyTimeoutRef.current = null;
      }
      setCopiedId(text);
      copyTimeoutRef.current = setTimeout(() => {
        copyTimeoutRef.current = null;
        setCopiedId(null);
      }, 2000);
    } catch (err) {
      console.error('Error al copiar:', err);
    }
  };

  return (
    <div className="item__sub__container">
      <Card>
        <div
          onClick={() => handleSubscriptionClick(id, version)}
          className="item__sub__content"
        >
          <div className="item__sub__title">
            <span className="title--bold row">
              <span>{productTitle}</span>
            </span>
            {shouldShowCancelButtons && (
              <div
                className="item__sub__actions"
                onClick={(e) => e.stopPropagation()}
              >
                <ButtonMoreOptions
                  id={id}
                  testid={`subscription-${id}`}
                  onClickCancel={
                    cancelOptions.showCancelActions
                      ? ({ isRefund }) => {
                          if (isRefund) {
                            handleCustomRefundClick();
                          } else {
                            handleWithoutRefundClick();
                          }
                        }
                      : undefined
                  }
                  labelCancelWithRefund={cancelOptions.labelCancelWithRefund}
                  labelCancelWithoutRefund={
                    cancelOptions.labelCancelWithoutRefund
                  }
                  onClickDetail={() => handleSubscriptionClick(id, version)}
                  onClickEdit={
                    !isInsurance &&
                    (user.isTester ||
                      user.agentAuthData?.role === 'customer service')
                      ? () => handleEditClick(id, version)
                      : undefined
                  }
                  label="detalles"
                  labelEdit="suscripción"
                  onClickRenewal={
                    !isInsurance && onRenewal
                      ? () => handleRenewalClick(id)
                      : undefined
                  }
                />
              </div>
            )}
          </div>
          <div className="product-id" onClick={(e) => e.stopPropagation()}>
            <span className="product-id__label">ID:</span>
            <span className="product-id__value">{id}</span>
            <button
              type="button"
              className="product-id__button"
              onClick={() => copyToClipboard(id)}
              title="Copiar ID"
            >
              {copiedId === id ? (
                <Check className="icon-check" />
              ) : (
                <Copy className="icon-copy" />
              )}
            </button>
          </div>
          <div className="product-id" onClick={(e) => e.stopPropagation()}>
            <span className="product-id__label">Compañía:</span>
            <span className="product-id__value">
              {company?.name?.toLowerCase() === 'connect pr'
                ? 'SODA'
                : company?.name ?? '—'}
            </span>
          </div>
          <div className="item__sub__detail">{`$${price} - ${dateExpire}`}</div>
          <div className="item__sub__status">
            <div className="item__sub__status__chips">
              <Chips label={typePeriodicity} />
              {hasTrial && <Chips backgroundColor="#FDE6E6" label="Trial" />}
            </div>
            <div className="item__sub__status__right">
              {providerImage && (
                <img
                  src={providerImage}
                  alt={providerProcessor}
                  className="item__sub__provider__image"
                />
              )}
              <ItemStatus
                status={status}
                message={messageStatus[status]}
              ></ItemStatus>
            </div>
          </div>
        </div>
      </Card>

      {/* Modal para cancelación sin reembolso */}
      <ModalConfirmation
        isOpen={showWithoutRefundModal}
        onClose={closeAllModals}
        onConfirm={handleWithoutRefundConfirm}
        title="Cancelar Suscripción"
        message="¿Estás seguro que deseas cancelar esta suscripción sin reembolso? Esta acción no se puede deshacer."
        confirmText="Sí, cancelar sin reembolso"
        cancelText="No, mantener suscripción"
        isLoading={isLoading}
      />

      {/* Modal para reembolso personalizado */}
      <ModalCustomRefund
        isOpen={showCustomRefundModal}
        onClose={closeAllModals}
        onConfirm={handleCustomRefundConfirm}
        isLoading={isLoading}
        maxAmount={price}
        subscriptionId={id}
        uid={uid}
        userImpersonationId={userImpersonationId}
      />

      {/* Modal de confirmación final */}
      <ModalConfirmation
        isOpen={showConfirmationModal}
        onClose={closeAllModals}
        onConfirm={executeCancellation}
        title="Confirmar Acción"
        message={
          confirmationData?.type === 'withoutRefund'
            ? '¿Estás seguro que deseas cancelar esta suscripción sin reembolso?'
            : `¿Estás seguro que deseas realizar un reembolso de $${confirmationData?.amount}?`
        }
        confirmText="Sí, confirmar"
        cancelText="Cancelar"
        isLoading={isLoading}
      />
    </div>
  );
};
